import React, { useState } from "react";

function GalleryResults({ results, capturedPhoto, onReset }) {
  const [lightbox, setLightbox] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const { matches, total } = results;

  const getFilename = (urlStr, fallback) => {
    try {
      const parsed = new URL(urlStr);
      const parts = parsed.pathname.split('/');
      return parts[parts.length - 1] || fallback;
    } catch {
      return fallback;
    }
  };

  const handleDownloadAll = async () => {
    setIsDownloading(true);
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      try {
        const proxyUrl = `/api/download-original?url=${encodeURIComponent(match.url)}`;
        const response = await fetch(proxyUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = getFilename(match.image, `match_${i}.jpg`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        await new Promise(r => setTimeout(r, 150));
      } catch (err) {
        console.error("Error downloading", match.image, err);
      }
    }
    setIsDownloading(false);
  };

  const handleDownloadSingle = async (match, e) => {
    if (e) e.stopPropagation();
    try {
      const proxyUrl = `/api/download-original?url=${encodeURIComponent(match.url)}`;
      const response = await fetch(proxyUrl);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = getFilename(match.image, "match.jpg");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading", match.image, err);
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-10 gap-5 bg-bg2 p-6 rounded-2xl border border-borderBase">
        <div className="flex items-center gap-5">
          {capturedPhoto && (
            <img src={capturedPhoto} alt="You" className="w-20 h-20 object-cover rounded-full border-[3px] border-accentSolid shadow-glow flex-shrink-0" />
          )}
          <div>
            <h2 className="text-3xl font-extrabold">
              {total > 0 ? (
                <>Found <span className="bg-accent-gradient bg-clip-text text-transparent">{total}</span> matching images</>
              ) : (
                "No matches found"
              )}
            </h2>
            <p className="text-textMuted text-base mt-1">
              {total > 0
                ? "Your face was detected in these photos from the gallery."
                : "Your face wasn't found in the gallery. Try adjusting the lighting or angle."}
            </p>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          {total > 0 && (
            <button 
              className="bg-accent-gradient text-white px-7 py-3 rounded-xl text-base font-semibold cursor-pointer transition-all shadow-glow hover:-translate-y-0.5 hover:shadow-glow-strong active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none" 
              onClick={handleDownloadAll}
              disabled={isDownloading}
            >
              {isDownloading ? "Downloading..." : "↓ Download All"}
            </button>
          )}
          <button 
            className="bg-bg3 text-white border border-borderBase px-6 py-3 rounded-full text-[0.95rem] font-medium cursor-pointer transition-all hover:bg-white/5 hover:border-borderHover" 
            onClick={onReset}
          >
            ← Search Again
          </button>
        </div>
      </div>

      {total > 0 && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
          {matches.map((match, i) => (
            <div
              key={i}
              className="group relative rounded-xl overflow-hidden cursor-pointer bg-bg3 aspect-square border border-borderBase transition-all duration-300 animate-fadeIn opacity-0 hover:-translate-y-1.5 hover:scale-[1.02] hover:shadow-glow hover:border-accentSolid hover:z-10"
              style={{ animationDelay: `${Math.min(i * 0.05, 0.45)}s` }}
              onClick={() => setLightbox(match)}
            >
              <img
                src={match.url}
                alt={`Match ${i + 1}`}
                loading="lazy"
                className="w-full h-full object-cover block"
                onError={(e) => {
                  e.target.src = "https://placehold.co/300x300/111/fff?text=Not+Found";
                }}
              />
              <button 
                onClick={(e) => handleDownloadSingle(match, e)}
                className="absolute top-3 right-3 bg-black/60 text-white p-2 rounded-full backdrop-blur-sm border border-white/20 opacity-0 group-hover:opacity-100 transition-all hover:bg-accentSolid hover:scale-110"
                title="Download Image"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
              </button>
              <div className="absolute bottom-3 right-3 bg-[rgba(9,10,15,0.85)] text-white text-xs font-bold py-1 px-2.5 rounded-full backdrop-blur-sm border border-white/10 shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
                {match.similarity}%
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[999] p-5 animate-fadeIn" onClick={() => setLightbox(null)}>
          <div className="relative max-w-[90vw] max-h-[90vh] bg-bg rounded-2xl border border-borderBase overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.8)] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button 
              className="absolute top-4 right-4 bg-black/50 text-white border border-white/20 w-10 h-10 rounded-full cursor-pointer text-xl flex items-center justify-center transition-all backdrop-blur-sm hover:bg-accentSolid hover:scale-110 z-10" 
              onClick={() => setLightbox(null)}
            >
              ✕
            </button>
            <div className="overflow-auto flex-1 flex items-center justify-center bg-black/50">
              <img src={lightbox.url} alt="Match" className="block max-w-full max-h-[75vh] object-contain" />
            </div>
            <div className="flex justify-between items-center p-4 px-6 border-t border-borderBase bg-bg2">
              <div className="flex gap-6 text-[0.95rem] text-textMuted flex-wrap">
                <span>Similarity: <strong className="text-white">{lightbox.similarity}%</strong></span>
                <span>File: <strong className="text-white">{getFilename(lightbox.image, "image")}</strong></span>
              </div>
              <button 
                onClick={(e) => handleDownloadSingle(lightbox, e)}
                className="flex items-center gap-2 bg-accent-gradient text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-glow hover:shadow-glow-strong transition-all hover:-translate-y-0.5 active:translate-y-px"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GalleryResults;
